// Multipart upload client for large (50GB+) raw footage.
//
// It talks to a presigned-URL backend (the chadha-surf backend, once exposed):
//   POST {API}/uploads/create   {key, contentType}                -> {uploadId}
//   POST {API}/uploads/sign     {key, uploadId, partNumber}       -> {url}
//   POST {API}/uploads/complete {key, uploadId, parts[]}          -> {location}
//   POST {API}/uploads/abort    {key, uploadId}
//
// The browser PUTs each part straight to S3 (no proxying through the backend),
// so it scales to very large files. Set REACT_APP_UPLOAD_API to enable it.
import firebase from '../firebaseConfig';
import { runMultipart, PART_SIZE } from '../utils/multipart';
import { deviceFingerprint } from '../utils/fingerprint';

const API = process.env.REACT_APP_UPLOAD_API || '';
const MOVIES_CDN = (process.env.REACT_APP_MOVIES_CDN || '').replace(/\/$/, '');
const SESSION_PREFIX = 'cs_upload_session:';

export const isUploadConfigured = () => !!API;

const safeGet = (k) => {
  try {
    const raw = localStorage.getItem(k);
    return raw ? JSON.parse(raw) : null;
  } catch (_e) {
    return null;
  }
};
const uploadSession = (id) => ({
  load: () => safeGet(`${SESSION_PREFIX}${id}`),
  save: (s) => {
    try {
      localStorage.setItem(`${SESSION_PREFIX}${id}`, JSON.stringify(s));
    } catch (_e) {
      /* ignore */
    }
  },
  clear: () => {
    try {
      localStorage.removeItem(`${SESSION_PREFIX}${id}`);
    } catch (_e) {
      /* ignore */
    }
  },
});

// Public CDN URL (cdn.chadha.surf) for an object stored in the movies bucket.
export const cdnUrl = (key) => (MOVIES_CDN ? `${MOVIES_CDN}/${key}` : '');

async function authHeaders() {
  const user = firebase.auth().currentUser;
  const token = user ? await user.getIdToken() : null;
  return token ? { Authorization: `Bearer ${token}` } : {};
}

async function api(path, body) {
  const res = await fetch(`${API}${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...(await authHeaders()) },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    let detail = '';
    try {
      const body = await res.json();
      detail = body?.error ? ` — ${body.error}` : '';
    } catch {
      /* non-JSON body */
    }
    const err = new Error(`${path} failed: ${res.status}${detail}`);
    err.status = res.status;
    throw err;
  }
  return res.json();
}

// Uploads one file via S3 multipart, resilient to outages and resumable from
// the last finished part. onProgress(fraction 0..1); optional onState reports
// reconnect attempts; optional monitor receives live telemetry.
// bucket: 'raw' (default, footage) or 'movies' (films + soundtracks).
export async function uploadFile(
  file,
  { key, contentType, onProgress, onState, onRecognized, monitor, signal, bucket = 'raw' }
) {
  if (!isUploadConfigured()) {
    throw new Error(
      'Upload backend not configured. Set REACT_APP_UPLOAD_API once the ' +
        'presigned-URL service is deployed.'
    );
  }
  const resumeId = `${bucket}|${file.name}|${file.size}|${file.lastModified || 0}`;
  const { key: usedKey, result } = await runMultipart(file, {
    create: () => api('/uploads/create', { key, contentType: contentType || file.type, bucket }),
    sign: (uploadId, k, partNumber) => api('/uploads/sign', { key: k, uploadId, partNumber, bucket }),
    complete: (uploadId, k, parts) => api('/uploads/complete', { key: k, uploadId, parts, bucket }),
    listParts: (uploadId, k) => api('/uploads/parts', { key: k, uploadId, bucket }),
    session: uploadSession(resumeId),
    onProgress,
    onState,
    onRecognized,
    monitor,
    signal,
    fileId: resumeId,
    partSize: PART_SIZE,
  });
  // `usedKey` is authoritative (a resumed upload keeps its original key).
  return { ...(result || {}), key: usedKey };
}

// Abort an in-flight authed multipart upload (best effort) + clear checkpoint.
export async function abortUpload(file, { bucket = 'raw' } = {}) {
  const resumeId = `${bucket}|${file.name}|${file.size}|${file.lastModified || 0}`;
  const sess = uploadSession(resumeId);
  const saved = sess.load();
  sess.clear();
  if (saved?.uploadId) {
    await api('/uploads/abort', { key: saved.key, uploadId: saved.uploadId, bucket }).catch(() => {});
  }
}

// Server-observed uploader provenance (IP + geo-IP) for the signed-in user.
export async function getUploaderContext() {
  if (!API) return null;
  try {
    const res = await fetch(`${API}/uploader-context`, { headers: { ...(await authHeaders()) } });
    if (!res.ok) return null;
    return res.json();
  } catch {
    return null;
  }
}

// Full uploader metadata for an in-app upload: server-verified IP/geo-IP merged
// with this browser's device fingerprint. NO permission-gated collection — the
// geo here is derived server-side from the IP, never the browser GPS prompt.
export async function collectUploaderMeta() {
  const fingerprint = deviceFingerprint();
  const server = await getUploaderContext();
  return {
    ...(server || {}),
    capturedAt: Date.now(),
    client: {
      fingerprintId: fingerprint.id,
      userAgent: fingerprint.userAgent,
      platform: fingerprint.platform,
      vendor: fingerprint.vendor,
      languages: fingerprint.languages,
      timezone: fingerprint.timezone,
      screen: fingerprint.screen,
      cores: fingerprint.cores,
      deviceMemory: fingerprint.deviceMemory,
      touch: fingerprint.touch,
    },
    macAddress: null, // not obtainable from a browser
  };
}

export function buildKey(prefix, fileName) {
  const safe = fileName.replace(/[^\w.\-]+/g, '_');
  return `${prefix}${Date.now()}-${safe}`;
}

// Presigned GET URL for streaming raw footage from the private bucket.
export async function getAssetPlaybackUrl(key) {
  if (!isUploadConfigured()) {
    throw new Error('Playback backend not configured (REACT_APP_UPLOAD_API).');
  }
  const { url } = await api('/assets/playback', { key });
  return url;
}

// Presigned GET that downloads an asset with a friendly filename. Works for
// raw footage (bucket 'raw') and movies-bucket assets like songs ('movies').
export async function getAssetDownloadUrl({ key, name, bucket = 'raw' }) {
  if (!isUploadConfigured()) throw new Error('Download backend not configured.');
  const { url } = await api('/assets/download', { key, name, bucket });
  return url;
}

// Off-site backup status for a raw asset: which parts are mirrored to the
// Glacier backup, their storage class, and the live object's replication state.
// `parts` is the asset's {s3Key,name}[]; falls back to a single {key}.
export async function getAssetBackupStatus({ key, parts }) {
  if (!isUploadConfigured()) throw new Error('Backend not configured.');
  return api('/assets/backup-status', { key, parts });
}

// Trigger a browser download for an asset stored in S3.
export async function downloadAsset({ key, name, bucket = 'raw' }) {
  const url = await getAssetDownloadUrl({ key, name, bucket });
  const a = document.createElement('a');
  a.href = url;
  a.rel = 'noreferrer';
  a.style.display = 'none';
  document.body.appendChild(a);
  a.click();
  a.remove();
  return url;
}

// Permanently delete a single S3 object (used by "vaporize").
export async function deleteRemoteObject({ key, bucket = 'raw' }) {
  if (!isUploadConfigured()) throw new Error('Backend not configured.');
  return api('/assets/delete', { key, bucket });
}

// Permanently delete every object under a prefix (a whole trip folder).
export async function deleteRemotePrefix({ prefix, bucket = 'raw' }) {
  if (!isUploadConfigured()) throw new Error('Backend not configured.');
  return api('/assets/delete-prefix', { prefix, bucket });
}

// Copy already-uploaded trip footage into a movie's video (server-side S3 copy).
export async function copyFootageToMovie({ sourceKey, movieId, name }) {
  if (!isUploadConfigured()) throw new Error('Backend not configured.');
  const { key, playbackUrl } = await api('/movies/from-footage', {
    sourceKey,
    movieId,
    name,
  });
  return { key, playbackUrl: playbackUrl || cdnUrl(key) };
}

// Export a full-resolution still from a raw clip at a given timestamp.
export async function exportFrameGrab({ s3Key, timeSec, format = 'jpg' }) {
  if (!isUploadConfigured()) throw new Error('Backend not configured.');
  return api('/assets/frame-grab', { s3Key, timeSec, format });
}

// Build a DaVinci Resolve round-trip bundle (FCPXML + LUTs + media manifest)
// for a movie. Returns { movieTitle, files:[{path,content}], media, warnings }.
export async function requestResolveExport(movieId) {
  if (!isUploadConfigured()) throw new Error('Backend not configured.');
  return api('/movies/resolve-export', { movieId });
}

// Enqueue a "Pro finish" render on the headless Resolve Studio node. Requires
// the node to be provisioned (scripts/resolve-node/). Returns { jobId }.
export async function requestResolveFinish(movieId, output) {
  if (!isUploadConfigured()) throw new Error('Backend not configured.');
  return api('/movies/resolve-finish', { movieId, output });
}

// Best-effort geocode of a place name to { lat, lng, label }.
export async function geoSearch(q) {
  if (!isUploadConfigured()) throw new Error('Backend not configured.');
  const res = await fetch(`${API}/geo/search?q=${encodeURIComponent(q)}`, {
    headers: { ...(await authHeaders()) },
  });
  if (!res.ok) throw new Error(`geo search failed: ${res.status}`);
  const data = await res.json();
  return data.results || [];
}

// Search YouTube via the backend proxy (key stays server-side).
export async function searchYouTube(q) {
  if (!isUploadConfigured()) throw new Error('Search backend not configured.');
  const res = await fetch(
    `${API}/music/youtube/search?q=${encodeURIComponent(q)}`,
    { headers: { ...(await authHeaders()) } }
  );
  if (res.status === 503) throw new Error('YouTube is not configured yet.');
  if (!res.ok) throw new Error(`search failed: ${res.status}`);
  const data = await res.json();
  return data.results || [];
}
